import moment from "moment";

/**
 * 공지사항 팝업 노출 억제 상태 관리 및 팝업 기간 시각 변환
 *
 * [노출 억제] 두 단계로 억제한다.
 *  1) 닫기(X)      : 현재 브라우저 세션 내 재표출 억제 → sessionStorage
 *  2) 다시 보지 않음 : 해당 공지의 팝업 종료일시까지 억제 → cookie (만료일시 = 팝업 종료일시)
 * 로그인 화면은 비로그인 상태로 서버 세션이 없으므로 두 화면 모두 브라우저 기준으로 처리한다.
 *
 * [시각 기준] POPUP_STR_DTM / POPUP_END_DTM 은 UTC로 저장하고 서버는 GETUTCDATE()와 비교한다.
 * 운영자와 사용자의 시간대가 달라도 같은 시점에 노출되도록 하기 위한 것이다.
 * 주의 - 같은 테이블의 CRE_DTM / UPD_DTM 은 GETDATE()(DB 서버 로컬) 기준이므로 이 두 컬럼과 직접 비교하지 않는다.
 * 화면 입력·표시는 항상 브라우저 로컬 시각이며, 전송 직전과 수신 직후에 아래 함수로 변환한다.
 */

/** datetime-local 입력 형식 (브라우저 로컬 시각) */
const INPUT_FORMAT = "YYYY-MM-DDTHH:mm";
/** 서버 전송 형식 (UTC, LocalDateTime 파싱 대상) */
const SERVER_FORMAT = "YYYY-MM-DDTHH:mm:ss";

/** 입력값(로컬 시각) → 서버 저장용 UTC 문자열 */
export const toUtcDateTime = (localValue) =>
  localValue ? moment(localValue, INPUT_FORMAT).utc().format(SERVER_FORMAT) : null;

/** 서버의 UTC 시각 → datetime-local 입력 형식(로컬 시각) */
export const toLocalInputDateTime = (utcValue) =>
  utcValue ? moment.utc(utcValue).local().format(INPUT_FORMAT) : "";

/** 서버의 UTC 시각 → 로컬 moment 객체 (현재 시각과 비교할 때 사용) */
export const utcToLocalMoment = (utcValue) =>
  utcValue ? moment.utc(utcValue).local() : null;

/**
 * 세션 내 닫은 공지 목록을 담는 sessionStorage 키.
 * 토큰 재발급 시 sessionStorage 전체를 비우므로 api.js에서 이 키를 보존한다.
 */
export const POPUP_SESSION_KEY = "noticePopupClosedIds";

const SESSION_KEY = POPUP_SESSION_KEY;
const COOKIE_PREFIX = "noticePopupHide_";

/** 팝업 노출 화면 구분 (PDI_COMM_NOTICE.POPUP_TARGET) */
export const POPUP_TARGET = {
  LOGIN: "LOGIN",
  MAIN: "MAIN",
};

/**
 * 억제 키는 노출 화면과 공지를 함께 식별한다.
 * 하나의 공지는 노출 화면이 하나뿐이지만, 운영자가 노출 화면을 변경한 경우
 * 이전 화면에서 닫은 기록이 새 화면까지 막지 않도록 화면을 키에 포함한다.
 */
const suppressKey = (target, id) => `${target}_${id}`;

const readSessionClosedKeys = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (error) {
    return [];
  }
};

/** 닫기 선택 시 현재 세션 내 재표출을 억제한다. */
export const addSessionClosedId = (target, id) => {
  try {
    const keys = readSessionClosedKeys();
    const key = suppressKey(target, id);
    if (!keys.includes(key)) {
      keys.push(key);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(keys));
    }
  } catch (error) {
    // sessionStorage 사용이 불가한 환경에서는 억제하지 않고 진행한다.
  }
};

const isClosedInSession = (target, id) =>
  readSessionClosedKeys().includes(suppressKey(target, id));

const isHiddenByCookie = (target, id) => {
  const key = `${COOKIE_PREFIX}${suppressKey(target, id)}=`;
  return document.cookie
    .split(";")
    .some((item) => item.trim().startsWith(key) && item.trim().endsWith("Y"));
};

/**
 * 다시 보지 않음 설정. 쿠키 만료일시를 해당 공지의 팝업 종료일시로 지정한다.
 * 종료일시를 알 수 없으면 만료를 지정하지 않아 브라우저 세션 종료 시 함께 사라진다.
 */
export const setHideCookie = (target, id, popupEndDtm) => {
  let expires = "";
  if (popupEndDtm) {
    // 서버가 내려주는 종료일시는 UTC이므로 UTC로 파싱한다.
    const endDate = moment.utc(popupEndDtm);
    if (endDate.isValid()) {
      expires = `; expires=${endDate.toDate().toUTCString()}`;
    }
  }
  document.cookie = `${COOKIE_PREFIX}${suppressKey(target, id)}=Y; path=/${expires}`;
};

/** 세션 내 닫기 및 다시 보지 않음 설정을 반영해 표출 대상만 남긴다. */
export const filterVisibleNotices = (target, list) =>
  (Array.isArray(list) ? list : []).filter(
    (notice) =>
      !isClosedInSession(target, notice.id) && !isHiddenByCookie(target, notice.id)
  );
